import { forwardRef, useState, useEffect } from 'react';
import cn from 'classnames';
import IconRadioChecked from '@/shared/assets/icons/radio-checked.svg';
import IconRadioUnchecked from '@/shared/assets/icons/radio-unchecked.svg';
import IconCheck from '@/shared/assets/icons/check.svg';
import { Svg } from '@/shared/ui/Svg';
import { BaseFieldProps } from '../BaseField';

interface CheckboxFieldProps extends BaseFieldProps {
  checked?: boolean;
  radioIcon?: boolean;
  size?: '16px';
  onChange?: (checked: boolean) => void;
}

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  (
    {
      name,
      label,
      error,
      disabled,
      labelClassName,
      checked,
      onChange,
      radioIcon,
      className,
      size = '4',
      ...props
    },
    ref,
  ) => {
    const [isChecked, setIsChecked] = useState(checked || false);

    useEffect(() => {
      // eslint-disable-next-line
      setIsChecked(checked || false);
    }, [checked]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;
      setIsChecked(newChecked);
      if (onChange) {
        onChange(newChecked);
      }
    };

    const sizeStyle = `w-${size} h-${size}`;

    return (
      <label
        htmlFor={name}
        className={cn(
          `flex items-center gap-3 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`,
          className,
        )}
      >
        <div className={cn(`relative ${sizeStyle}`)}>
          <input
            {...props}
            ref={ref}
            type="checkbox"
            id={name}
            name={name}
            disabled={disabled}
            checked={isChecked}
            onChange={handleChange}
            className="sr-only"
          />
          {radioIcon ? (
            isChecked ? (
              <Svg Icon={IconRadioChecked} className={sizeStyle} />
            ) : (
              <Svg Icon={IconRadioUnchecked} className={sizeStyle} />
            )
          ) : (
            <span
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-colors duration-300 ${sizeStyle} border ${disabled ? 'border-gray-400' : !isChecked && error ? 'text-red-500' : 'border-black'} rounded-[6px] overflow-hidden ${isChecked && disabled ? 'text-gray-400' : isChecked ? 'bg-black' : 'bg-transparent'} `}
            >
              <Svg
                Icon={IconCheck}
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${sizeStyle} text-white ${isChecked ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
              />
            </span>
          )}
        </div>
        <span
          className={cn(
            `text-[14px] leading-[1.4] tracking-normal transition-colors duration-300 ${disabled ? 'text-gray-400' : !isChecked && error ? 'text-red-500' : 'text-custom-black'}`,
            labelClassName,
          )}
        >
          {label}
        </span>
      </label>
    );
  },
);

CheckboxField.displayName = 'CheckboxField';
